import { memo, type ComponentPropsWithoutRef } from "react";
import { useIntl, FormattedMessage } from "react-intl";

import { useForm } from "common/core/form";
import { defaultRequiredMessage, FormattedFieldError } from "common/core/form/error";
import { RadioLabel, RadioGroup, RadioInput } from "common/core/form/option";
import { userFullName } from "util/user";

import { Form, Fields, Actions } from "./form";

export type FormValues = {
  meetingParticipantId: string;
};
type Props = {
  onSubmit: (formValues: FormValues) => void;
  onBack: ComponentPropsWithoutRef<typeof Actions>["onBack"];
  primarySignerParticipants: {
    id: string;
    firstName: null | string;
    lastName: null | string;
  }[];
};

function ChooseSignerPartyForWitness({ onSubmit, onBack, primarySignerParticipants }: Props) {
  const form = useForm<FormValues>();
  const intl = useIntl();
  return (
    <Form onSubmit={form.handleSubmit(onSubmit)}>
      <Fields>
        <RadioGroup
          label={
            <FormattedMessage
              id="9cd6fcd9-9a78-4ee6-98e3-a0ec724878a5"
              defaultMessage="Which signer is the witness located with?"
            />
          }
          groupError={
            <FormattedFieldError
              inputName="meetingParticipantId"
              error={form.formState.errors.meetingParticipantId}
            />
          }
        >
          {primarySignerParticipants.map((participant) => (
            <RadioLabel
              key={participant.id}
              label={userFullName(participant)}
              radio={
                <RadioInput<FormValues["meetingParticipantId"]>
                  value={participant.id}
                  {...form.register("meetingParticipantId", {
                    required: defaultRequiredMessage(intl),
                  })}
                />
              }
            />
          ))}
        </RadioGroup>
      </Fields>

      <Actions onBack={onBack}>
        <FormattedMessage id="83749215-e3e0-4b63-97b1-c1ae084086c8" defaultMessage="Continue" />
      </Actions>
    </Form>
  );
}

const MemoizedChooseSignerPartyForWitness = memo(ChooseSignerPartyForWitness);

function SignerAlertConfirmation({ onSubmit }: { onSubmit: () => void }) {
  return (
    <Form onSubmit={onSubmit}>
      <Fields>
        <FormattedMessage
          id="fbb13158-fb37-453a-9932-10a2a55fd01c"
          defaultMessage="We are collecting the witness' information with the signer."
          tagName="p"
        />
      </Fields>

      <Actions>
        <FormattedMessage
          id="f496ba16-e2ec-4158-b03b-b67e72e1a8d7"
          defaultMessage="Return to Meeting"
        />
      </Actions>
    </Form>
  );
}

const MemoizedSignerAlertConfirmation = memo(SignerAlertConfirmation);

export {
  MemoizedChooseSignerPartyForWitness as ChooseSignerPartyForWitness,
  MemoizedSignerAlertConfirmation as SignerAlertConfirmation,
};
